import React, { useState, useEffect } from 'react';
import { BringzzButton, BringzzText } from "@bringzz/components";
import { useNavigation } from 'context/NavigationContext';
import {
    BringzzPageContainer,
    BringzzIcon,
    BringzzRadio,
    BringzzBottomDrawer,
    BringzzRadioList
} from "@bringzz/components";
import { useLocation } from 'react-router-dom';
import useRequest from 'hooks/useRequest';
import { useAuth } from 'context/AuthContext';
import { useTranslation } from 'react-i18next';

let state = {
    checked: (
        <div></div>
    ),
    unChecked: (
        <div></div>
    ),
};

const ConsumerPicturePage = () => {
    const { backPress } = useNavigation();
    const { navigate } = useNavigation();
    const { user, refreshUserData } = useAuth()
    const { t } = useTranslation();
    const location = useLocation();

    const [pickerDrawerOpened, setPickerDrawerOpened] = useState(false);
    const [profileImage, setProfileImage] = useState(user.userPic || null);
    const [profilePictureUrl, setProfilePictureUrl] = useState(null);
    const [imageChanged, setImageChanged] = useState(false);

    const { sendRequest, data, error, loading } = useRequest();

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const profilePicture = urlParams.get('profilePicture');

        if (profilePicture) {
            const decodedUrl = decodeURIComponent(profilePicture);
            console.log('Setting profile picture from URL:', decodedUrl);
            setProfilePictureUrl(decodedUrl);
            setProfileImage(decodedUrl);
        }
    }, [location.search]);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileImage(reader.result);
                setImageChanged(true);
            };
            reader.readAsDataURL(file);
            postImage(file);
        }
    };

    const downloadImageFromUrl = async (url) => {
        try {
            // Create an image element and convert it to blob via canvas
            // This avoids CORS and rate limit issues with direct fetch
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.referrerPolicy = 'no-referrer';

                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);

                    canvas.toBlob((blob) => {
                        if (blob) {
                            const file = new File([blob], "profile.jpg", { type: 'image/jpeg' });
                            resolve(file);
                        } else {
                            reject(new Error('Failed to create blob from image'));
                        }
                    }, 'image/jpeg', 0.95);
                };

                img.onerror = (error) => {
                    console.error('Error loading image:', error);
                    reject(error);
                };

                img.src = url;
            });
        } catch (error) {
            console.error('Error downloading image from URL:', error);
            return null;
        }
    };

    const saveProfilePictureFromUrl = async () => {
        if (profilePictureUrl && !imageChanged) {
            try {
                const file = await downloadImageFromUrl(profilePictureUrl);
                if (file) {
                    await postImage(file);
                }
            } catch (error) {
                console.error('Failed to save profile picture from URL:', error);
                // Continue navigation even if image save fails
            }
        }
    };

    const postImage = async (file) => {
        const formData = new FormData();
        formData.append('profilePicture', file);

        await sendRequest({
            method: 'POST',
            url: `/module/profile/changeProfilePicture`,
            data: formData,
            headers: {
                'Content-Type': undefined
            }
        })
            .then(response => {
                if (response.status === 'success') {
                    refreshUserData()
                    return response
                };
            })
            .catch(error => {
                console.error(error);
                return false;
            });
    };

    return (
        <BringzzPageContainer
            className="bg-white"
            steps={true}
            activeStep={2}
            header={{
                left: (
                    <BringzzIcon
                        size='22'
                        icon='IconChevronLeft'
                        folder='LineIcons'
                        className='cursor-pointer'
                        onClick={backPress}
                    />
                ),
                title: t('component.auth.consumer.picture.header.title'),
                right: <BringzzButton size='sm' className="border border-black" onClick={() => {
                    navigate(`/register/consumer/insurance`);
                }}>{t('component.auth.consumer.picture.header.skip')}</BringzzButton>
            }}
            footer={
                <div className='pb-8 px-6'>
                    <BringzzButton
                        size='lg'
                        className='bg-magic-lilac w-full flex justify-center text-midnight-blue'
                        onClick={async () => {
                            await saveProfilePictureFromUrl();
                            navigate(`/register/consumer/insurance`);
                        }}
                        disabled={!profileImage || loading || error}
                    >
                        {t('component.auth.consumer.picture.buttons.continue')}
                    </BringzzButton>
                </div>
            }
            authenticated={false}
        >
            <div className='relative flex justify-center bg-magic-lilac/20'>
                <div className='relative bg-white w-full bg-cover bg-center flex flex-col'>
                    {/* Content */}
                    <div className='flex-grow flex flex-col py-8 px-4'>
                        <div className='z-10 flex flex-col '>
                            <div className='pl-2 flex flex-col space-y-6 md:max-w-lg lg:max-w-xl xl:max-w-2xl'>
                                <BringzzText tag='h1' className='pl-2 text-midnight-blue font-bold'>
                                    <div className='flex flex-col'><div>{t('component.auth.consumer.picture.title.choose')}</div><div>{t('component.auth.consumer.picture.title.your_profile_picture')}</div></div>
                                </BringzzText>
                            </div>
                            <div className='space-y-6 mt-12 flex justify-center'>
                                <div className='space-y-4 relative'>
                                    {profileImage ? (
                                        <img
                                            src={profileImage}
                                            alt="Profile"
                                            className='rounded-full border-8 border-magic-lilac h-48 w-48 object-cover'
                                            referrerPolicy="no-referrer"
                                        />
                                    ) : (
                                        <div className='flex justify-center border-8 border-magic-lilac text-white font-bold text-4xl items-center rounded-full bg-midnight-blue h-48 w-48'>P</div>
                                    )}
                                    <div className='bg-magic-lilac h-12 w-12 absolute bottom-0 rounded-full right-2' onClick={() => setPickerDrawerOpened(true)}>
                                        <BringzzIcon
                                            size='24'
                                            icon='IconPlus'
                                            folder='LineIcons'
                                            className='cursor-pointer flex h-full flex-col items-center justify-center'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <BringzzBottomDrawer isOpen={pickerDrawerOpened} title={t('component.auth.consumer.picture.upload.title')} close={() => setPickerDrawerOpened(false)}>
                        <div className='px-4 py-6'>
                            <BringzzRadioList
                                state={state}
                                className="rounded-xl w-full text-center"
                                onChange={() => setPickerDrawerOpened(false)}
                            >
                                <BringzzRadio value="Camera"
                                    className="p-2 py-4 hover:bg-slate- border-1 border-t border-gray-400 flex"
                                    onClick={() => {
                                        setPickerDrawerOpened(false);
                                        document.getElementById('fileInput').click();
                                    }}
                                >
                                    <div>
                                        <BringzzText tag='h3'>{t('component.auth.consumer.picture.upload.camera')}</BringzzText>
                                    </div>
                                </BringzzRadio>
                                <BringzzRadio value="Photos"
                                    className="p-2 py-4 hover:bg-slate- border-1 border-t border-gray-400"
                                    onClick={() => {
                                        setPickerDrawerOpened(false);
                                        document.getElementById('fileInput').click();
                                    }}
                                >
                                    <div>
                                        <BringzzText tag='h3'>{t('component.auth.consumer.picture.upload.photos')}</BringzzText>

                                    </div>
                                </BringzzRadio>
                                <BringzzRadio className="p-2 py-4 hover:bg-slate- border-1 border-t border-gray-400" value="File"
                                    onClick={() => {
                                        setPickerDrawerOpened(false);
                                        document.getElementById('fileInput').click();
                                    }}
                                >
                                    <div>
                                        <BringzzText tag='h3'>{t('component.auth.consumer.picture.upload.file')}</BringzzText>
                                    </div>
                                </BringzzRadio>
                            </BringzzRadioList>
                            <input
                                type="file"
                                id="fileInput"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleFileSelect}
                            />
                        </div>
                    </BringzzBottomDrawer>
                </div>
            </div>
        </BringzzPageContainer>
    );
};

export default ConsumerPicturePage;
